import {Component, OnInit, Input, OnDestroy} from '@angular/core';
import {NgbDate, NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {RouteService} from '../../shared/services/route.service';
import {Stop} from '../../shared/models/stop.model';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {Route} from '../../shared/models/route.model';
import {DataStorageService} from '../../shared/services/data-storage.service';
import {RouteGroup} from '../../shared/models/routeGroup.model';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {ToolsService} from '../../shared/services/tools.service';
import {SendEmailService} from '../../shared/services/send-email.service';
import {DatePipe} from '@angular/common';
import {Ticket, TicketUpdate} from '../../shared/models/ticket.model';
import {StopTitlePipe} from '../../shared/pipes/stop-title.pipe';

@Component({
    selector: 'form-edit-route-bulk',
    templateUrl: 'form-edit-route-bulk.html',
    styleUrls: ['../../../assets/scss/plugins/_datepicker.scss', './form-edit-route-bulk.scss']
})

export class FormEditRouteBulkComponent implements OnInit, OnDestroy {

    @Input() routes: Route[];
    @Input() routeGroupId: number;

    routeBulkForm: FormGroup;
    stops: Stop[];
    routeGroups: RouteGroup[];
    routeGroupOptions: any = [];
    pickStopOptions: any = [];
    dropStopOptions: any = [];

    selectConfig: any = {
        labelField: 'label',
        valueField: 'value',
        searchField: ['label']
    };

    stopSelectConfig: any = {
        labelField: 'label',
        valueField: 'value',
        searchField: ['label']
    };

    date: { year: number, month: number };

    is_please_wait: boolean;

    other_new_stop_changed = new Subject<number>();

    tickets_to_change: Ticket[];

    route_options: Array<{
        id: number,
        time: string,
        stop: number,
        start_date: string
    }> = [];

    private unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        private routeService: RouteService,
        private dataStorageService: DataStorageService,
        private ngDateParserFormatter: NgbDateParserFormatter,
        private sendEmailService: SendEmailService,
        private toolsService: ToolsService,
        private datePipe: DatePipe) {
        this.is_please_wait = false;
    }

    ngOnInit() {

        this.stops = this.routeService.getStop();
        this.routeGroups = this.routeService.getRouteGroup();
        this.initRouteGroupOptions();
        this.initStopOptions();
        this.initForm();

        this.routeService.stopsChanged
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            (stops: Stop[]) => {
                this.stops = this.routeService.getStop();
                this.initStopOptions();
            }
        );

        this.routeService.routeGroupsChanged
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            (routeGroups: RouteGroup[]) => {
                this.routeGroups = routeGroups;
                this.initRouteGroupOptions();
            }
        );

        this.routeBulkForm.get('start_date').valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
                (value) => {
                    const new_start_date_raw = value.year + '-' + value.month + '-' + value.day;
                    const new_start_date = this.datePipe.transform(new_start_date_raw, 'yyyy-MM-dd');
                    if (this.routeBulkForm.get('end_date').value !== '') {
                        const new_end_date_obj = this.routeBulkForm.get('end_date').value;
                        const new_end_date_raw = new_end_date_obj.year + '-' + new_end_date_obj.month + '-' + new_end_date_obj.day;
                        const new_end_date = this.datePipe.transform(new_end_date_raw, 'yyyy-MM-dd');
                        // call existing ticket info
                        this.dataStorageService.fetchTicketsSchedule(new_start_date, new_end_date, this.routeGroupId)
                            .pipe(takeUntil(this.unsubscribe$))
                            .subscribe(
                                (val) => {
                                    this.setOptions(val);
                                }
                            );
                    }
                }
            );

        this.routeBulkForm.get('end_date').valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
                (value) => {
                    const new_end_date_raw = value.year + '-' + value.month + '-' + value.day;
                    const new_end_date = this.datePipe.transform(new_end_date_raw, 'yyyy-MM-dd');
                    if (this.routeBulkForm.get('start_date').value !== '') {
                        const new_start_date_obj = this.routeBulkForm.get('start_date').value;
                        const new_start_date_raw = new_start_date_obj.year + '-' + new_start_date_obj.month + '-' + new_start_date_obj.day;
                        const new_start_date = this.datePipe.transform(new_start_date_raw, 'yyyy-MM-dd');
                        // call existing ticket info
                        this.dataStorageService.fetchTicketsSchedule(new_start_date, new_end_date, this.routeGroupId)
                            .pipe(takeUntil(this.unsubscribe$))
                            .subscribe(
                                (val) => {
                                    this.setOptions(val);
                                }
                            );
                    }
                }
            );
    }

    initForm() {
        this.routeBulkForm = new FormGroup({
            'start_date': new FormControl('', [Validators.required]),
            'end_date': new FormControl('', [Validators.required]),
            'route_option': new FormControl('', [Validators.required])
        });
    }

    initRouteGroupOptions() {
        for (const routeGroup of this.routeGroups) {
            this.routeGroupOptions.push({'label': routeGroup.title, 'value': routeGroup.id, 'code': routeGroup.title});
        }
    }

    initStopOptions() {
        for (const stop of this.stops) {
            this.pickStopOptions.push({'label': stop.title, 'value': stop.id, 'code': stop.title});
            this.dropStopOptions.push({'label': stop.title, 'value': stop.id, 'code': stop.title});
        }
    }

    setOptions(tickets: Ticket[]) {
        if (tickets && tickets.length !== 0) {
            this.tickets_to_change = tickets.slice();
            const is_ob = this.routeGroupId % 2 !== 0;
            if (is_ob) {
                const first_idle_stop_route_id = this.tickets_to_change.filter(ticket => +ticket.route.pick_stop_id === 1)[0].route_id;
                const idle_stop_id = this.tickets_to_change.filter(ticket => +ticket.route.pick_stop_id === 1 &&
                    +ticket.route_id !== +first_idle_stop_route_id).length > 0 ? 1 : 2;
                for (const ticket of tickets) {
                    if (+ticket.route.pick_stop_id === idle_stop_id) {
                        if (this.route_options.filter(option => +option.id === +ticket.route_id).length === 0) {
                            this.route_options.push({
                                id: ticket.route_id, time: ticket.route.pick_time
                                , stop: ticket.route.pick_stop_id, start_date: ticket.route.start_date
                            });
                        }
                    }
                }
            } else {
                const first_idle_stop_route_id = this.tickets_to_change.filter(ticket => +ticket.route.drop_stop_id === 1)[0].route_id;
                const idle_stop_id = tickets.filter(ticket => +ticket.route.drop_stop_id === 1 &&
                    +ticket.route_id !== +first_idle_stop_route_id).length > 0 ? 1 : 2;
                for (const ticket of tickets) {
                    if (+ticket.route.drop_stop_id === idle_stop_id) {
                        if (this.route_options.filter(option => +option.id === +ticket.route_id).length === 0) {
                            this.route_options.push({
                                id: ticket.route_id, time: ticket.route.drop_time
                                , stop: ticket.route.drop_stop_id, start_date: ticket.route.start_date
                            });
                        }
                    }
                }
            }
        }
    }

    onCheckChange(value) {
        this.routeBulkForm.get('route_option').patchValue(value);
    }

    onSubmit() {

        const is_ob = this.routeGroupId % 2 !== 0;

        // get selected route.
        const route_value = this.routeBulkForm.value.route_option;
        const start_date = this.route_options.filter(option => +option.id === +route_value)[0].start_date;

        const newTicketsUpdate = [];

        for (const ticket_to_change of this.tickets_to_change) {
            let stop = ticket_to_change.route.pick_stop_id;
            let new_route_id = this.routes.filter(route => +route.pick_stop_id === +stop && route.start_date === start_date)[0].id;

            if (!is_ob) {
                stop = ticket_to_change.route.drop_stop_id;
                new_route_id = this.routes.filter(route => +route.drop_stop_id === +stop && route.start_date === start_date)[0].id;
            }

            if (+ticket_to_change.route_id !== +new_route_id) {
                const newTicket =
                    new TicketUpdate(
                        ticket_to_change.id, ticket_to_change.date, new_route_id,
                        ticket_to_change.reservation_id, ticket_to_change.status);
                newTicketsUpdate.push(newTicket);
            }
        }

        this.dataStorageService.updateTickets(newTicketsUpdate)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(res => {
                if (res['error']) {
                    alert('Error');
                } else {
                    for (const new_ticket of newTicketsUpdate) {
                        this.dataStorageService.fetchReservation(new_ticket.reservation_id)
                            .pipe(takeUntil(this.unsubscribe$))
                            .subscribe(resservationObj => {
                                // get new schedule
                                const new_route =
                                    this.routes.filter(route => +route.id === +new_ticket.route_id)[0];
                                const schedule = new_ticket.date + ', ' +
                                new StopTitlePipe(this.routeService).transform(is_ob ? new_route.pick_stop_id : new_route.drop_stop_id)
                                    + ', ' + is_ob ? new_route.pick_time : new_route.drop_time;
                                this.sendEmailToPassenger(resservationObj, schedule);
                            });
                    }
                }
            });
    }

    sendEmailToPassenger(reservation, schedule) {
        const emailObj = {
            type: 9,
            email_to: reservation.email,
            reservation: reservation,
            schedule: schedule
        };
        this.sendEmailService.initEmail(emailObj);
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
